import React from "react";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { ServicePortalImageGenerator } from "./ServicePortalImageGenerator";
interface BrandingStepProps {
  portalImageUrl?: string | null;
  hasPortalImage?: boolean;
  portalImageError?: string;
  onPortalImageSelect?: (file?: File) => void;
  onPortalImageRemove?: () => void;
  serviceName?: string;
  organisationName?: string;
  serviceDescription?: string;
  portalAddress?: string;
  portalAttributes?: string[];
  portalPublished?: boolean;
  canGeneratePortalImage?: boolean;
  colorHex: string;
  logoUrl: string | null;
  onColourChange: (value: string) => void;
  onLogoSelect: (file?: File) => void;
  onLogoRemove: () => void;
}

export function ServiceBrandingStep({
  colorHex,
  logoUrl,
  onColourChange,
  onLogoSelect,
  onLogoRemove,
  portalImageUrl, hasPortalImage, portalImageError, onPortalImageSelect, onPortalImageRemove,
  serviceName, organisationName, serviceDescription, portalAddress, portalAttributes = [], portalPublished = false,
  canGeneratePortalImage = false,
}: BrandingStepProps) {
  return (
    <div>
      <div className="card-title">Choose your branding</div>
      <section className="card service-branding-section">
        <div className="card-title">Community portal image</div>
        <p className="field-hint">Add a session background. Landscape PNG, JPEG or WebP, up to 5 MB.</p>
        <div className="button-row button-row-left">
          <label className="action-button compact service-logo-upload">
            {hasPortalImage ? "Replace portal image" : "Choose portal image"}
            <input type="file" aria-label="Community portal image" accept="image/png,image/jpeg,image/webp"
              onChange={event => { onPortalImageSelect?.(event.target.files?.[0]); event.target.value = ""; }} />
          </label>
          {hasPortalImage && <button type="button" className="action-button compact secondary" onClick={onPortalImageRemove}>Remove portal image</button>}
        </div>
        {portalImageError && <p role="alert" className="field-hint">{portalImageError}</p>}
        {canGeneratePortalImage && onPortalImageSelect && <ServicePortalImageGenerator serviceName={serviceName ?? ""} description={serviceDescription ?? ""} onSelect={onPortalImageSelect} />}
        <p className="field-hint">Image changes take effect when you save the service. Removing the image restores the default background.</p>
      </section>
      <section className="card service-branding-section">
        <div className="card-title">Portal view</div>
        <div className="field-hint">{portalPublished ? "Service published" : "Service unpublished"}</div>
        <p className="field-hint">Card preview. Scheduled sessions show their date and time. Publish both the service and organisation to appear on the portal.</p>
        <article className="service-portal-preview" aria-label="Community portal card preview" style={{
          background: portalImageUrl ? `linear-gradient(90deg, rgba(255,255,255,.96), rgba(255,255,255,.90) 45%, rgba(255,255,255,.58)), url("${portalImageUrl}") center / cover` : "linear-gradient(155deg, #f1faf5, #ffffff 65%)",
        }}>
          <div className="service-portal-preview-accent" />
          <div className="service-portal-preview-body">
            <div className="service-portal-preview-provider">{logoUrl ? <img src={logoUrl} alt="" /> : <span aria-hidden="true">{organisationName?.slice(0, 1) || "S"}</span>}<div><strong>{organisationName || "Your organisation"}</strong><small>Community session</small></div></div>
            <div className="service-portal-preview-date">Session date</div>
            <h3 title={serviceName}>{serviceName || "Service name"}</h3>
            <p>◷ Session time</p>
            <p>{portalAddress || "Venue details"}</p>
            <details className="service-portal-preview-details"><summary>More information <span aria-hidden="true">+</span></summary>
              {serviceDescription && <p>{serviceDescription}</p>}
              <div className="service-portal-preview-tags">{portalAttributes.map((name, index) => <span key={`${name}-${index}`}>{name}</span>)}</div>
              {!portalAttributes.length && <p>No additional service attributes selected.</p>}
            </details>
          </div>
        </article>
      </section>
      <section className="card service-branding-section">
        <div className="card-title">Logo</div>
        <div className="field-hint">Optional image for this service</div>
        <div className="service-logo-editor">
          <div className="service-logo-preview">
            {logoUrl ? (
              <img src={logoUrl} alt="Service logo preview" />
            ) : (
              <ImageOutlinedIcon aria-label="No logo selected" />
            )}
          </div>
          <div className="service-logo-controls">
            <strong>{logoUrl ? "Logo selected" : "Choose a logo"}</strong>
            <span>PNG, JPEG or WebP, up to 2 MB.</span>
            <div className="button-row button-row-left">
              <label className="action-button compact service-logo-upload">
                {logoUrl ? "Replace" : "Choose logo"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) => onLogoSelect(event.target.files?.[0])}
                />
              </label>
              {logoUrl && (
                <button
                  type="button"
                  className="action-button compact secondary"
                  onClick={onLogoRemove}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
      <section className="card service-branding-section">
        <div className="card-title">Service colour</div>
        <div className="field-hint">Used on service cards and icons</div>
        <label className="service-branding-colour-picker">
          <input
            type="color"
            value={colorHex}
            onChange={(event) => onColourChange(event.target.value)}
            aria-label="Choose service colour"
          />
          <span>
            <strong>Service colour</strong>
            <small>Tap the colour to change it</small>
          </span>
          <code>{colorHex}</code>
        </label>
      </section>
    </div>
  );
}
